import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '../api/client';

interface CloudConfig {
  id: string;
  nome: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  accessTokenMasked: string;
  verifyToken: string;
  appSecretMasked: string;
  wabaId: string;
  appId: string | null;
  tokenExpiraEm: string | null;
  templateName: string | null;
  templateLanguage: string | null;
  templateCategory: string | null;
  ativo: boolean;
  templateOnly: boolean;
  autoReplyMessage: string | null;
  autoReplyVariacoes: string[];
  optOutText: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Status da validade do token pra pintar aviso no card. */
function tokenExpiryStatus(iso: string | null): {
  label: string;
  tone: 'ok' | 'warn' | 'danger';
} | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const dias = Math.floor((ms - Date.now()) / 86_400_000);
  const data = new Date(iso).toLocaleDateString('pt-BR');
  if (dias < 0) return { label: `expirou em ${data}`, tone: 'danger' };
  if (dias <= 15) return { label: `expira em ${dias}d (${data})`, tone: 'danger' };
  if (dias <= 45) return { label: `expira em ${dias}d (${data})`, tone: 'warn' };
  return { label: `válido até ${data}`, tone: 'ok' };
}

export function WhatsappCanaisPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: configs, isLoading } = useQuery<CloudConfig[]>({
    queryKey: ['cloud-configs'],
    queryFn: () => request<CloudConfig[]>('/admin/whatsapp-cloud/configs'),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      request(`/admin/whatsapp-cloud/configs/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cloud-configs'] }),
  });

  return (
    <section className="space-y-6 max-w-4xl">
      <header>
        <h1 className="h1">Canais — WhatsApp Cloud API (Meta)</h1>
        <p className="muted text-sm mt-1">
          Configurações da API Oficial do WhatsApp. Cada item é um número Cloud conectado. Sara
          recebe/envia mensagens pelos números aqui configurados além das instâncias Evolution
          existentes.
        </p>
        <p className="text-xs text-sara-text-subtle mt-2">
          Webhook URL pra colar no painel Meta:{' '}
          <code className="px-1 rounded bg-sara-surface-hover">
            https://tarot.suportesam.com/api/webhooks/whatsapp-cloud
          </code>
          . Verify token = o que você cadastrar abaixo.
        </p>
      </header>

      <div className="flex justify-end">
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setCreating(true);
            setEditingId(null);
          }}
        >
          + Adicionar número Cloud
        </button>
      </div>

      {creating && (
        <ConfigForm
          mode="create"
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['cloud-configs'] });
          }}
        />
      )}

      {isLoading ? (
        <div className="card muted">Carregando…</div>
      ) : !configs || configs.length === 0 ? (
        <div className="card muted text-sm">
          Nenhum canal cadastrado ainda. Use o botão acima pra adicionar.
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((c) =>
            editingId === c.id ? (
              <ConfigForm
                key={c.id}
                mode="edit"
                config={c}
                onCancel={() => setEditingId(null)}
                onSaved={() => {
                  setEditingId(null);
                  void queryClient.invalidateQueries({ queryKey: ['cloud-configs'] });
                }}
              />
            ) : (
              <article key={c.id} className="card space-y-2">
                <header className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-sara-text">{c.nome}</h2>
                    <p className="text-xs muted">
                      {formatPhone(c.displayPhoneNumber)} ·{' '}
                      <code className="px-1 rounded bg-sara-surface-hover text-xs">
                        {c.phoneNumberId}
                      </code>
                    </p>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap justify-end">
                    {(() => {
                      const st = tokenExpiryStatus(c.tokenExpiraEm);
                      if (!st) return null;
                      const cls =
                        st.tone === 'danger'
                          ? 'bg-sara-danger/15 text-sara-danger'
                          : st.tone === 'warn'
                            ? 'bg-amber-500/15 text-amber-600'
                            : 'bg-sara-success/15 text-sara-success';
                      return (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${cls}`}
                          title="Validade do access token — renove antes de expirar pra o número não parar de disparar."
                        >
                          🔑 {st.label}
                        </span>
                      );
                    })()}
                    {c.templateOnly && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full bg-sara-accent/15 text-sara-accent"
                        title="Número dedicado a disparo de templates — Sara não responde mensagens recebidas aqui."
                      >
                        📣 Disparo
                      </span>
                    )}
                    {c.ativo ? (
                      <span className="text-xs text-sara-success">✓ Ativo</span>
                    ) : (
                      <span className="text-xs text-sara-text-subtle">○ Inativo</span>
                    )}
                  </div>
                </header>
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <div>
                    <dt className="muted">WABA ID</dt>
                    <dd className="font-mono">{c.wabaId}</dd>
                  </div>
                  <div>
                    <dt className="muted">Verify Token</dt>
                    <dd className="font-mono break-all">{c.verifyToken}</dd>
                  </div>
                  <div>
                    <dt className="muted">Access Token</dt>
                    <dd className="font-mono">{c.accessTokenMasked}</dd>
                  </div>
                  <div>
                    <dt className="muted">App Secret</dt>
                    <dd className="font-mono">{c.appSecretMasked}</dd>
                  </div>
                  {c.appId && (
                    <div>
                      <dt className="muted">App ID</dt>
                      <dd className="font-mono break-all">{c.appId}</dd>
                    </div>
                  )}
                  {c.templateOnly && c.templateName && (
                    <div>
                      <dt className="muted">Template de disparo</dt>
                      <dd className="font-mono break-all">
                        {c.templateName}
                        {c.templateLanguage ? ` (${c.templateLanguage})` : ''}
                      </dd>
                    </div>
                  )}
                  {c.templateOnly && c.autoReplyVariacoes.length > 0 && (
                    <div>
                      <dt className="muted">Variações auto-resposta</dt>
                      <dd>{c.autoReplyVariacoes.length} cadastradas</dd>
                    </div>
                  )}
                </dl>
                <footer className="flex gap-2 pt-2 border-t border-sara-border">
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() => {
                      setEditingId(c.id);
                      setCreating(false);
                    }}
                  >
                    ✏ Editar
                  </button>
                  <TestConnectionButton configId={c.id} />
                  <button
                    type="button"
                    className="btn-danger text-xs ml-auto"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remover "${c.nome}"? Conversas existentes nesse canal continuam mas Sara não responderá pelo nº.`,
                        )
                      ) {
                        remove.mutate(c.id);
                      }
                    }}
                  >
                    Remover
                  </button>
                </footer>
              </article>
            ),
          )}
        </div>
      )}
    </section>
  );
}

type TestResult =
  | { ok: true; displayPhoneNumber: string; verifiedName?: string; quality?: string }
  | { ok: false; status: number; error: string };

function TestConnectionButton({ configId }: { configId: string }): JSX.Element {
  const test = useMutation({
    mutationFn: () =>
      request<TestResult>(`/admin/whatsapp-cloud/configs/${configId}/test`, { method: 'POST' }),
  });
  return (
    <>
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={() => test.mutate()}
        disabled={test.isPending}
        aria-busy={test.isPending}
      >
        {test.isPending ? '🔄 Testando…' : '🔌 Testar conexão'}
      </button>
      {test.data && (
        <span
          className={`text-xs px-2 py-1 rounded ${
            test.data.ok
              ? 'bg-sara-success/15 text-sara-success'
              : 'bg-sara-danger/15 text-sara-danger'
          }`}
          title={
            test.data.ok
              ? `Verified: ${test.data.verifiedName ?? '?'} · Quality: ${test.data.quality ?? '?'}`
              : test.data.error.slice(0, 200)
          }
        >
          {test.data.ok ? `✓ ${test.data.displayPhoneNumber}` : `✗ HTTP ${test.data.status}`}
        </span>
      )}
    </>
  );
}

function ConfigForm({
  mode,
  config,
  onCancel,
  onSaved,
}: {
  mode: 'create' | 'edit';
  config?: CloudConfig;
  onCancel: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [nome, setNome] = useState(config?.nome ?? '');
  const [phoneNumberId, setPhoneNumberId] = useState(config?.phoneNumberId ?? '');
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState(config?.displayPhoneNumber ?? '');
  const [accessToken, setAccessToken] = useState('');
  const [verifyToken, setVerifyToken] = useState(config?.verifyToken ?? '');
  const [appSecret, setAppSecret] = useState('');
  const [wabaId, setWabaId] = useState(config?.wabaId ?? '');
  const [appId, setAppId] = useState(config?.appId ?? '');
  // <input type="date"> quer YYYY-MM-DD; guardamos o ISO no envio.
  const [tokenExpiraEm, setTokenExpiraEm] = useState(
    config?.tokenExpiraEm ? config.tokenExpiraEm.slice(0, 10) : '',
  );
  const [templateName, setTemplateName] = useState(config?.templateName ?? '');
  const [templateLanguage, setTemplateLanguage] = useState(config?.templateLanguage ?? 'pt_BR');
  const [ativo, setAtivo] = useState(config?.ativo ?? true);
  const [templateOnly, setTemplateOnly] = useState(config?.templateOnly ?? false);
  // Cada variação é uma mensagem COMPLETA (pode ter várias linhas). Editadas
  // como uma lista de caixas separadas — nada de "uma por linha" (quebrava
  // mensagens multi-linha). Semeia com as existentes, ou a msg legada, ou 1 vazia.
  const [variacoes, setVariacoes] = useState<string[]>(() => {
    const v = config?.autoReplyVariacoes ?? [];
    if (v.length > 0) return v;
    if (config?.autoReplyMessage) return [config.autoReplyMessage];
    return [''];
  });
  const [optOutText, setOptOutText] = useState(config?.optOutText ?? '');

  const variacoesArr = variacoes.map((s) => s.trim()).filter((s) => s.length > 0);

  function setVariacaoAt(i: number, value: string): void {
    setVariacoes((prev) => prev.map((v, idx) => (idx === i ? value : v)));
  }
  function addVariacao(): void {
    setVariacoes((prev) => [...prev, '']);
  }
  function removeVariacao(i: number): void {
    setVariacoes((prev) => (prev.length <= 1 ? [''] : prev.filter((_, idx) => idx !== i)));
  }
  const tokenExpiraIso = tokenExpiraEm
    ? new Date(`${tokenExpiraEm}T12:00:00Z`).toISOString()
    : null;

  const save = useMutation({
    mutationFn: () => {
      if (mode === 'create') {
        return request<CloudConfig>('/admin/whatsapp-cloud/configs', {
          method: 'POST',
          body: {
            nome,
            phoneNumberId,
            displayPhoneNumber,
            accessToken,
            verifyToken,
            appSecret,
            wabaId,
            appId: appId.trim() || null,
            tokenExpiraEm: tokenExpiraIso,
            templateName: templateOnly ? templateName.trim() || null : null,
            templateLanguage: templateOnly ? templateLanguage.trim() || null : null,
            templateOnly,
            autoReplyMessage: templateOnly ? (variacoesArr[0] ?? null) : null,
            autoReplyVariacoes: templateOnly ? variacoesArr : [],
            optOutText: templateOnly ? optOutText : null,
          },
        });
      }
      // edit: só manda campos com valor (skip vazios pra access/app secret)
      const body: Record<string, unknown> = {
        nome,
        displayPhoneNumber,
        verifyToken,
        wabaId,
        appId: appId.trim() || null,
        tokenExpiraEm: tokenExpiraIso,
        templateName: templateOnly ? templateName.trim() || null : null,
        templateLanguage: templateOnly ? templateLanguage.trim() || null : null,
        ativo,
        templateOnly,
        autoReplyMessage: templateOnly ? (variacoesArr[0] ?? null) : null,
        autoReplyVariacoes: templateOnly ? variacoesArr : [],
        optOutText: templateOnly ? optOutText : null,
      };
      if (accessToken.trim()) body.accessToken = accessToken;
      if (appSecret.trim()) body.appSecret = appSecret;
      return request<CloudConfig>(`/admin/whatsapp-cloud/configs/${config!.id}`, {
        method: 'PUT',
        body,
      });
    },
    onSuccess: onSaved,
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="card space-y-3" aria-label="Formulário canal Cloud">
      <h2 className="h2">{mode === 'create' ? 'Novo canal Cloud' : `Editar: ${config?.nome}`}</h2>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="cf-nome">
            Apelido (interno)
          </label>
          <input
            id="cf-nome"
            type="text"
            className="input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex: Sara Principal"
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="cf-phone-id">
            Phone Number ID (Meta)
          </label>
          <input
            id="cf-phone-id"
            type="text"
            className="input font-mono"
            value={phoneNumberId}
            onChange={(e) => setPhoneNumberId(e.target.value.replace(/\D/g, ''))}
            placeholder="123456789012345"
            disabled={mode === 'edit'}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="cf-display">
            Número E.164 (sem +)
          </label>
          <input
            id="cf-display"
            type="text"
            className="input"
            value={displayPhoneNumber}
            onChange={(e) => setDisplayPhoneNumber(e.target.value.replace(/\D/g, ''))}
            placeholder="5511999999999"
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="cf-waba">
            WABA ID
          </label>
          <input
            id="cf-waba"
            type="text"
            className="input font-mono"
            value={wabaId}
            onChange={(e) => setWabaId(e.target.value.replace(/\D/g, ''))}
            placeholder="987654321098765"
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="cf-appid">
            App ID (Meta) <span className="text-sara-text-subtle">— opcional</span>
          </label>
          <input
            id="cf-appid"
            type="text"
            className="input font-mono"
            value={appId}
            onChange={(e) => setAppId(e.target.value.replace(/\D/g, ''))}
            placeholder="1295852455952672"
          />
        </div>
        <div>
          <label className="label" htmlFor="cf-token-exp">
            Validade do token <span className="text-sara-text-subtle">— opcional</span>
          </label>
          <input
            id="cf-token-exp"
            type="date"
            className="input"
            value={tokenExpiraEm}
            onChange={(e) => setTokenExpiraEm(e.target.value)}
          />
          <p className="text-xs text-sara-text-subtle mt-1">
            Aviso no card quando estiver perto de expirar. Deixe em branco se for permanente.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor="cf-verify">
            Verify Token (cola no painel Meta)
          </label>
          <input
            id="cf-verify"
            type="text"
            className="input font-mono"
            value={verifyToken}
            onChange={(e) => setVerifyToken(e.target.value)}
            placeholder="ex: meu-token-secreto-123"
            required
            minLength={8}
          />
          <p className="text-xs text-sara-text-subtle mt-1">
            Esse mesmo valor vai no campo "Verify token" do webhook da Meta.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor="cf-access">
            Access Token (permanente — System User)
          </label>
          <input
            id="cf-access"
            type="password"
            className="input font-mono"
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            placeholder={mode === 'edit' ? '••• (deixe em branco pra manter o atual)' : 'EAAG...'}
            required={mode === 'create'}
            minLength={mode === 'create' ? 20 : 0}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor="cf-secret">
            App Secret (Meta)
          </label>
          <input
            id="cf-secret"
            type="password"
            className="input font-mono"
            value={appSecret}
            onChange={(e) => setAppSecret(e.target.value)}
            placeholder={mode === 'edit' ? '••• (deixe em branco pra manter o atual)' : '32 chars'}
            required={mode === 'create'}
            minLength={mode === 'create' ? 16 : 0}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-sara-border bg-sara-bg p-3">
        <div>
          <p className="text-sm font-medium text-sara-text">📣 Só disparo (não responde com IA)</p>
          <p className="text-xs muted">
            {templateOnly
              ? 'Número dedicado a disparar templates. Sara NÃO responde mensagens recebidas — elas vão pro Chatwoot e /chats pra resposta manual.'
              : 'Número normal: Sara responde automaticamente as mensagens recebidas.'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={templateOnly}
          onClick={() => setTemplateOnly((v) => !v)}
          className="toggle"
          data-state={templateOnly ? 'on' : 'off'}
        >
          <span className="toggle-thumb" aria-hidden="true" />
        </button>
      </div>

      {templateOnly && (
        <div className="space-y-3 rounded-lg border border-sara-accent/30 bg-sara-accent/5 p-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="label" htmlFor="cf-template">
                Template de disparo <span className="text-sara-text-subtle">— auto-detecta</span>
              </label>
              <input
                id="cf-template"
                type="text"
                className="input font-mono text-sm"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="deixe em branco: a reativação detecta o aprovado"
              />
            </div>
            <div>
              <label className="label" htmlFor="cf-template-lang">
                Idioma
              </label>
              <input
                id="cf-template-lang"
                type="text"
                className="input font-mono text-sm"
                value={templateLanguage}
                onChange={(e) => setTemplateLanguage(e.target.value)}
                placeholder="pt_BR"
              />
            </div>
          </div>
          <p className="text-xs text-sara-text-subtle -mt-1">
            Cada número dispara com o SEU template. Deixe em branco que o sistema pega o aprovado
            com variáveis da Meta e preenche sozinho. O disparo é dividido (round-robin) entre todos
            os números marcados como “Só disparo” e ativos.
          </p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="label mb-0">Mensagens automáticas (pós-resposta ao disparo)</label>
              <span className="text-xs text-sara-text-subtle">
                {variacoesArr.length} ativa{variacoesArr.length === 1 ? '' : 's'}
              </span>
            </div>
            <p className="text-xs text-sara-text-subtle">
              Enviada 1x quando a pessoa responde o template (clica em “Continuar!” ou escreve). O
              sistema escolhe UMA por pessoa (a mesma pessoa sempre recebe a mesma), pra não mandar
              texto idêntico pra todo mundo. Cada caixa é uma mensagem completa, pode ter várias
              linhas. Lembre: a Sara NÃO atende por esse número, então inclua o link/《número》
              dela.
            </p>
            {variacoes.map((v, i) => (
              <div key={i} className="rounded-lg border border-sara-border bg-sara-bg p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-sara-text-muted">Mensagem {i + 1}</span>
                  <button
                    type="button"
                    className="btn-ghost text-xs text-sara-danger"
                    onClick={() => removeVariacao(i)}
                    aria-label={`Remover mensagem ${i + 1}`}
                  >
                    ✕ remover
                  </button>
                </div>
                <textarea
                  className="input text-sm min-h-[110px]"
                  value={v}
                  onChange={(e) => setVariacaoAt(i, e.target.value)}
                  placeholder={
                    'Olá! 🌙\n\nToque no link pra falar com a Sara: https://curtinho.link/sara\n\nOu salve e chame: (11) 95213-5686\n\nUm abraço, equipe Semente Energética. 💛'
                  }
                />
              </div>
            ))}
            <button type="button" className="btn-ghost text-xs" onClick={addVariacao}>
              + Adicionar variação
            </button>
            <p className="text-xs text-sara-text-subtle">
              Deixe tudo em branco pra NÃO enviar auto-resposta nenhuma.
            </p>
          </div>
          <div>
            <label className="label" htmlFor="cf-optout">
              Texto do botão de opt-out (não enviar a mensagem se a pessoa clicar nele)
            </label>
            <input
              id="cf-optout"
              className="input text-sm"
              value={optOutText}
              onChange={(e) => setOptOutText(e.target.value)}
              placeholder="NÃO RECEBER MAIS MENSAGENS"
            />
            <p className="text-xs text-sara-text-subtle mt-1">
              Deve ser igual ao texto do botão no template. Em branco usa detecção padrão (“não
              receber”, “parar”, “sair”…).
            </p>
          </div>
        </div>
      )}

      {mode === 'edit' && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-sara-border bg-sara-bg p-3">
          <div>
            <p className="text-sm font-medium text-sara-text">Ativo</p>
            <p className="text-xs muted">
              {ativo ? 'Sara recebe e envia por esse nº.' : 'Webhooks ignorados, envio bloqueado.'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={ativo}
            onClick={() => setAtivo((v) => !v)}
            className="toggle"
            data-state={ativo ? 'on' : 'off'}
          >
            <span className="toggle-thumb" aria-hidden="true" />
          </button>
        </div>
      )}

      {save.isError && (
        <p role="alert" className="text-sara-danger text-sm">
          {save.error instanceof ApiError ? save.error.message : 'Erro ao salvar.'}
        </p>
      )}

      <div className="flex gap-2 justify-end pt-2 border-t border-sara-border">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancelar
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={save.isPending}
          aria-busy={save.isPending}
        >
          {save.isPending ? 'Salvando…' : mode === 'create' ? 'Criar canal' : 'Salvar alterações'}
        </button>
      </div>
    </form>
  );
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return `+${digits}`;
}
